import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetItemQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { formatDateOnly, formatDateTime } from '../../../utils/helpers';
import { useLanguage } from '../../../context/LanguageContext';

const AdminItemView: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useGetItemQuery(id!);

  if (isLoading) {
    return <PageLoader />;
  }

  if (error || !data?.data) {
    return <div className="alert alert-danger m-5">{t('Item not found')}</div>;
  }

  const item = data.data;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">{t('Item Details')}</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Home')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/items'); }} className="text-muted text-hover-primary">{t('Items')}</a>
                </li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <button className="btn btn-primary" onClick={() => navigate(`/admin/items/${id}/edit`)}>
                {t('Edit Item')}
              </button>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <div className="row g-9 my-3">
                  <div className="col-md-6">
                    <label className="fs-6 fw-semibold mb-2">{t('Name')}</label>
                    <div className="form-control form-control-solid">{item.name}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="fs-6 fw-semibold mb-2">{t('Name (Arabic)')}</label>
                    <div className="form-control form-control-solid">{item.name_ar || t('N/A')}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="fs-6 fw-semibold mb-2">{t('Category')}</label>
                    <div className="form-control form-control-solid">{item.category?.title || t('N/A')}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="fs-6 fw-semibold mb-2">{t('Status')}</label>
                    <div>
                      <span className={`badge badge-light-${item.status === 1 ? 'success' : 'danger'}`}>
                        {item.status === 1 ? t('Active') : t('Inactive')}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <label className="fs-6 fw-semibold mb-2">{t('Description')}</label>
                    <div className="form-control form-control-solid" style={{ minHeight: '100px' }}>{item.description || t('N/A')}</div>
                  </div>
                  <div className="col-md-12">
                    <label className="fs-6 fw-semibold mb-2">{t('Description (Arabic)')}</label>
                    <div className="form-control form-control-solid" style={{ minHeight: '100px' }}>{item.description_ar || t('N/A')}</div>
                  </div>
                  {item.images && item.images.length > 0 && (
                    <div className="col-md-12">
                      <label className="fs-6 fw-semibold mb-2">{t('Images')}</label>
                      <div className="d-flex flex-wrap gap-3">
                        {item.images.map((image: string, index: number) => (
                          <img 
                            key={index}
                            src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}/uploads/${image}`} 
                            alt={`${item.name} ${index + 1}`} 
                            className="w-150px h-150px rounded object-cover" 
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="card-footer d-flex justify-content-end py-6 px-9">
                  <button type="button" className="btn btn-light btn-active-light-primary me-2" onClick={() => navigate('/admin/items')}>
                    {t('Back')}
                  </button>
                  <button type="button" className="btn btn-primary" onClick={() => navigate(`/admin/items/${id}/edit`)}>
                    {t('Edit Item')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminItemView;

